/** What happened to a file, as seven steps - v5 stage F2 (`docs/design/v5-build-scope.md`).
 *
 * **A presentation of the document's real state, not a second state machine.** Nothing here moves a
 * document, and the only inputs are what the server has already said: the row (`state`, the linked
 * figures, the uploader's site hint) and, once it exists, the review (how many fields were found).
 * A step is `done` only on a fact; a step that needs a person is `waiting`, never `current`, so a
 * screen cannot show a spinner over a thing that is waiting for somebody.
 *
 * **Seven steps, and "Checked" is not "scanned".** The prototype's second step is *scanned*; the
 * server's own words (`evidence/stages.py`) say a `validated` document had its type, size and
 * encryption accepted and a PDF's active content handled, and that this is **not an antivirus scan**.
 * A step called *scanned* would make that claim for us, so it is called *Checked*.
 *
 * **`ready` is `verified` and nothing else** (ADR-0165). A document with its fields read is not
 * ready: it is waiting for a named person, which is the product's promise, and the trail says so by
 * stopping at `waiting`.
 *
 * **The match is a person's.** Nothing a document says is resolved to a row (SEC-05), so *Matched*
 * is `done` only when the uploader named a site (the hint) or the document was accepted; before
 * that it is `waiting` for the person who reviews, who chooses the site and meter there.
 */

import type { DocumentRow, Review } from "../../api/evidence";

export const TRAIL_STEPS = [
  { key: "received", label: "Received" },
  { key: "checked", label: "Checked" },
  { key: "read", label: "Read" },
  { key: "values", label: "Values found" },
  { key: "matched", label: "Matched" },
  { key: "figure", label: "Figure calculated" },
  { key: "ready", label: "Accepted" },
] as const;

export type StepKey = (typeof TRAIL_STEPS)[number]["key"];

/** `waiting` is a step that needs a person; `current` is one the system is on. */
export type StepStatus = "done" | "current" | "waiting" | "failed" | "todo";

export interface TrailStep {
  key: StepKey;
  label: string;
  status: StepStatus;
  /** One short phrase of metadata (never a paragraph), or `null`. */
  detail: string | null;
}

export interface TrailInput {
  /** `null` while the file is still being sent: nothing exists on the server yet. */
  document: Pick<
    DocumentRow,
    "state" | "state_reason" | "linked_figures" | "facility_hint_name" | "refusal_remedy"
  > | null;
  /** The review, when it has been read. Without it, *Values found* follows the state alone. */
  review?: Pick<Review, "items"> | null;
}

export interface Trail {
  steps: readonly TrailStep[];
  /** The step a person should look at: the first that is not done. `null` when all are. */
  at: StepKey | null;
  /** True when the trail ended badly (refused, set aside) rather than simply being unfinished. */
  stopped: boolean;
}

const ORDER: Record<string, number> = {
  received: 0,
  validated: 1,
  extracting: 2,
  awaiting_review: 3,
  verified: 4,
};

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** The seven steps for one file. Pure, and total over the document states. */
export function trailOf(input: TrailInput): Trail {
  const { document, review } = input;
  const steps: Record<StepKey, { status: StepStatus; detail: string | null }> = {
    received: { status: "todo", detail: null },
    checked: { status: "todo", detail: null },
    read: { status: "todo", detail: null },
    values: { status: "todo", detail: null },
    matched: { status: "todo", detail: null },
    figure: { status: "todo", detail: null },
    ready: { status: "todo", detail: null },
  };
  let stopped = false;

  if (document === null) {
    steps.received = { status: "current", detail: "Sending" };
  } else if (document.state === "quarantined") {
    stopped = true;
    steps.received.status = "done";
    steps.checked = { status: "failed", detail: "Could not be read" };
  } else if (document.state === "rejected") {
    stopped = true;
    steps.received.status = "done";
    steps.checked.status = "done";
    steps.read.status = "done";
    steps.values.status = "done";
    steps.ready = { status: "failed", detail: "Set aside by a person" };
  } else if (document.state === "superseded") {
    for (const key of Object.keys(steps) as StepKey[]) steps[key].status = "done";
    steps.ready.detail = "Replaced by a corrected document";
  } else {
    const at = ORDER[document.state] ?? 0;
    steps.received.status = "done";
    if (at === 0) steps.checked = { status: "current", detail: null };
    if (at >= 1) steps.checked.status = "done";
    // Reading is a person's act (the redaction gate is on it), so a checked document is waiting.
    if (at === 1) steps.read = { status: "waiting", detail: "Ready to read" };
    if (at === 2) steps.read = { status: "current", detail: "Reading" };
    if (at >= 3) steps.read.status = "done";
    if (at >= 3) {
      const found = review ? review.items.filter((item) => !item.missing).length : null;
      const all = review ? review.items.length : null;
      steps.values = {
        status: "done",
        detail: found !== null && all !== null ? `${found} of ${all}` : null,
      };
      if (document.state === "awaiting_review") {
        if (document.facility_hint_name) {
          steps.matched = { status: "done", detail: document.facility_hint_name };
        } else {
          steps.matched = { status: "waiting", detail: "Choose the site in review" };
        }
        steps.figure = { status: "todo", detail: null };
        steps.ready = { status: "waiting", detail: "Needs a person to accept" };
      }
    }
    if (at === 4) {
      steps.matched = { status: "done", detail: document.facility_hint_name ?? null };
      const figures = document.linked_figures;
      steps.figure = {
        status: "done",
        detail: typeof figures === "number" ? plural(figures, "figure") : null,
      };
      steps.ready = { status: "done", detail: null };
    }
  }

  const list: TrailStep[] = TRAIL_STEPS.map(({ key, label }) => ({
    key,
    label,
    status: steps[key].status,
    detail: steps[key].detail,
  }));
  const first = list.find((step) => step.status !== "done");
  return { steps: list, at: first ? first.key : null, stopped };
}

/** How many steps are done, for a compact "4 of 7". */
export function doneCount(trail: Trail): number {
  return trail.steps.filter((step) => step.status === "done").length;
}
